import React from 'react';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Shield, 
  FileText, 
  Users, 
  Lock, 
  ArrowLeft 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Terms & Conditions</h1>
              <p className="text-gray-600 mt-2">
                Old Guards Basketball Club - Membership Terms and Conditions
              </p>
            </div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="space-y-6">
          {/* Membership Application */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-basketball-orange-500" />
                <span>1. Membership Application</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Application Process:</strong> By submitting an application, you agree to become a member of the Old Guards Basketball Club (OGBC). Membership is subject to approval by the Club's Executive Committee.
                </p>
                <p>
                  <strong>Information Accuracy:</strong> All information provided in your application must be accurate and truthful. The Club reserves the right to verify any information provided and may request additional documentation.
                </p>
                <p>
                  <strong>Membership Benefits:</strong> Membership benefits begin only after approval and payment of applicable fees. The Club will notify you of your application status within 7 business days.
                </p>
                <p>
                  <strong>Membership Duration:</strong> Membership is valid for one year from the date of approval and requires annual renewal. Monthly fees are due by the 5th of each month.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Constitution & Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-basketball-orange-500" />
                <span>2. Constitution, Rules & Code of Conduct</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Club Constitution:</strong> All members must abide by the Club's Constitution, which governs the organization and operation of OGBC. The Constitution is available for review upon request.
                </p>
                <p>
                  <strong>Club Rules:</strong> Members are expected to follow all Club rules, including but not limited to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Facility usage guidelines and operating hours</li>
                  <li>Training schedules and attendance requirements</li>
                  <li>Equipment care and maintenance responsibilities</li>
                  <li>Guest and visitor policies</li>
                </ul>
                <p>
                  <strong>Code of Conduct:</strong> The Code of Conduct promotes respect, sportsmanship, and positive behavior both on and off the court. This includes:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Respectful treatment of all members, staff, and visitors</li>
                  <li>Fair play and sportsmanship during all activities</li>
                  <li>Appropriate language and behavior at all times</li>
                  <li>Compliance with anti-discrimination policies</li>
                </ul>
                <p>
                  <strong>Violations:</strong> Violations of the Constitution, Rules, or Code of Conduct may result in warnings, suspension, or termination of membership depending on the severity and frequency of violations.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Risk & Insurance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-basketball-orange-500" />
                <span>3. Assumption of Risk & Personal Insurance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Inherent Risks:</strong> Basketball is a contact sport that involves inherent risks of injury, including but not limited to sprains, fractures, concussions, and other serious injuries. Participation in basketball activities carries these risks.
                </p>
                <p>
                  <strong>Risk Acknowledgment:</strong> By participating in Club activities, you acknowledge and assume all risks associated with basketball and related activities. This includes training sessions, games, tournaments, and social events.
                </p>
                <p>
                  <strong>Personal Insurance:</strong> The Club strongly recommends that all members maintain adequate personal health and accident insurance coverage. Members are responsible for their own medical expenses resulting from injuries.
                </p>
                <p>
                  <strong>Medical Clearance:</strong> You are responsible for ensuring your physical fitness and medical clearance to participate in basketball activities. The Club may require medical certificates for certain activities.
                </p>
                <p>
                  <strong>Club Liability:</strong> The Club, its officers, coaches, volunteers, and other members are not liable for any injuries sustained during Club activities. This limitation applies to all Club-sponsored events and activities.
                </p>
                <p>
                  <strong>Emergency Procedures:</strong> Medical emergencies will be handled according to Club protocols, but members are responsible for their own medical expenses. The Club will provide basic first aid and emergency response as available.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Data */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5 text-basketball-orange-500" />
                <span>4. Privacy & Data Protection</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Data Collection:</strong> The Club collects and stores personal information necessary for membership administration and club operations, including:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Personal details (name, contact information, date of birth)</li>
                  <li>Emergency contact information</li>
                  <li>Medical information relevant to participation</li>
                  <li>Payment and financial information</li>
                  <li>Photos and videos for promotional purposes (with consent)</li>
                </ul>
                <p>
                  <strong>Data Usage:</strong> Your data will be used for:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Club communications and announcements</li>
                  <li>Membership management and billing</li>
                  <li>Event coordination and scheduling</li>
                  <li>Emergency situations and medical response</li>
                  <li>Club promotion and marketing (with consent)</li>
                </ul>
                <p>
                  <strong>Data Security:</strong> The Club implements appropriate security measures to protect your personal information from unauthorized access, disclosure, or misuse. This includes secure storage systems and limited access protocols.
                </p>
                <p>
                  <strong>Data Sharing:</strong> Personal data will not be shared with third parties without your consent, except as required by law or for emergency medical situations.
                </p>
                <p>
                  <strong>Your Rights:</strong> You have the right to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Request access to your personal data</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your data (subject to legal requirements)</li>
                  <li>Withdraw consent for marketing communications</li>
                </ul>
                <p>
                  <strong>Data Retention:</strong> Personal data will be retained for as long as necessary for membership purposes and legal requirements. Former members' data may be retained for historical and legal purposes.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Additional Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-basketball-orange-500" />
                <span>5. Additional Terms</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  <strong>Payment Terms:</strong> Membership fees are due monthly and must be paid by the 5th of each month. Late payments may result in suspension of membership privileges.
                </p>
                <p>
                  <strong>Facility Access:</strong> Members have access to club facilities during operating hours. Facility access may be restricted during private events or maintenance periods.
                </p>
                <p>
                  <strong>Equipment Usage:</strong> Club equipment must be used responsibly and returned in good condition. Members may be charged for damaged or lost equipment.
                </p>
                <p>
                  <strong>Termination:</strong> Membership may be terminated by either party with 30 days written notice. The Club reserves the right to terminate membership immediately for serious violations.
                </p>
                <p>
                  <strong>Changes to Terms:</strong> These terms may be updated from time to time. Members will be notified of significant changes via email or club announcements.
                </p>
                <p>
                  <strong>Governing Law:</strong> These terms are governed by the laws of Tanzania. Any disputes will be resolved through the appropriate legal channels in Tanzania.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="flex-1"
          >
            Return to Home
          </Button>
          <Button
            asChild
            className="flex-1 bg-basketball-gradient hover:opacity-90"
          >
            <Link to="/register">
              Proceed to Registration
            </Link>
          </Button>
        </div>

        {/* Contact Information */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Questions About These Terms?</h3>
              <p className="text-sm text-gray-600">
                If you have questions about these terms or need clarification on any club policies, please contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="outline" asChild>
                  <a href="mailto:info@ogbasketball.co.tz">Email: info@ogbasketball.co.tz</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="tel:+255621728109">Call: +255 621 728 109</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms; 